import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Music2, Pause, Play, Square } from "lucide-react";

const MINIMIZED_KEY = "cr-music-minimized";
const FADE_TARGET = 0.4;

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const startedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [minimized, setMinimized] = useState(() => {
    try {
      return localStorage.getItem(MINIMIZED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const minimize = () => {
    setMinimized(true);
    try {
      localStorage.setItem(MINIMIZED_KEY, "1");
    } catch {}
  };

  const maximize = () => {
    setMinimized(false);
    try {
      localStorage.setItem(MINIMIZED_KEY, "0");
    } catch {}
  };

  const startFade = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    let v = 0;
    audio.volume = 0;
    fadeRef.current = setInterval(() => {
      v = Math.min(FADE_TARGET, v + 0.02);
      audio.volume = v;
      if (v >= FADE_TARGET) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
      }
    }, 100);
  };

  const stopFade = () => {
    if (fadeRef.current) {
      clearInterval(fadeRef.current);
      fadeRef.current = null;
    }
  };

  const play = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio
      .play()
      .then(() => {
        setPlaying(true);
        startFade();
      })
      .catch(() => {});
  };

  const pause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    stopFade();
    audio.pause();
    setPlaying(false);
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    stopFade();
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    play();

    const unlock = () => {
      if (audio.paused) play();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });

    const onPlay = () => {
      setPlaying(true);
      if (!startedRef.current) {
        startedRef.current = true;
        window.dispatchEvent(new CustomEvent("cr:music-started"));
      }
    };
    const onPause = () => {
      setPlaying(false);
      stopFade();
    };
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    const pauseNow = () => {
      audio.pause();
    };
    window.addEventListener("cr:music-pause", pauseNow);

    return () => {
      stopFade();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("cr:music-pause", pauseNow);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (minimized) {
    return (
      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={maximize}
        className="glass fixed bottom-[7rem] left-4 z-[75] flex h-11 w-11 items-center justify-center rounded-full text-[#f8b818] shadow-[0_10px_30px_rgba(248,184,24,0.25)] transition-transform hover:scale-110 sm:bottom-6 sm:left-5"
        aria-label="Show the music controls"
        title="Show the music controls"
        data-testid="music-restore-btn"
      >
        <Music2 size={18} />
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.8, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed bottom-[7rem] left-4 z-[75] flex items-center gap-2 sm:bottom-6 sm:left-5"
      data-testid="music-player-widget"
    >
      <div className="glass flex items-center gap-1 rounded-full p-1.5" data-testid="music-transport-pill">
        <button
          onClick={play}
          className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
            playing
              ? "bg-[#f8b818] text-[#241c0d] shadow-[0_6px_18px_rgba(248,184,24,0.4)]"
              : "text-[#241c0d]/70 hover:bg-[#f8b818]/15 hover:text-[#c87700]"
          }`}
          aria-label="Play theme song"
          title="Play the Cool Runnings theme song"
          data-testid="music-play-btn"
        >
          {playing ? (
            <span className="flex items-end gap-[3px]" data-testid="music-equalizer">
              <span className="eq-bar h-3 w-[3px]" style={{ animationDelay: "0ms" }} />
              <span className="eq-bar h-5 w-[3px]" style={{ animationDelay: "150ms" }} />
              <span className="eq-bar h-4 w-[3px]" style={{ animationDelay: "300ms" }} />
            </span>
          ) : (
            <Play size={16} className="fill-current" />
          )}
        </button>
        <button
          onClick={pause}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[#241c0d]/70 transition-colors hover:bg-[#f8b818]/15 hover:text-[#c87700]"
          aria-label="Pause theme song"
          title="Pause the theme song"
          data-testid="music-pause-btn"
        >
          <Pause size={16} className="fill-current" />
        </button>
        <button
          onClick={stop}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[#241c0d]/70 transition-colors hover:bg-[#f8b818]/15 hover:text-[#c87700]"
          aria-label="Stop theme song and start over"
          title="Stop the theme song and start over"
          data-testid="music-stop-btn"
        >
          <Square size={13} className="fill-current" />
        </button>
      </div>

      <span
        className="glass hidden rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[#241c0d] sm:block"
        data-testid="music-player-label"
      >
        Control music
      </span>

      <button
        onClick={minimize}
        className="glass flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#241c0d]/70 transition-all hover:scale-105 hover:text-[#c87700]"
        aria-label="Minimise the music controls"
        title="Minimise the music controls"
        data-testid="music-minimize-btn"
      >
        <Minus size={14} />
      </button>

      <audio ref={audioRef} src="/audio/cool-runnings-day.mp3" loop preload="metadata" data-testid="theme-audio" />
    </motion.div>
  );
}
